import {
    type FC,
    type ReactNode,
    createContext,
    useContext,
    useMemo,
    useEffect,
    useState,
    useCallback,
    useRef,
} from 'react';
import { graphql, useRelayEnvironment, fetchQuery, useFragment } from 'react-relay';
import { getLocalRecentHistoryList } from 'dibs-recent-history/exports/getLocalRecentHistoryList.ts';
import { COLLECTIONS_COLORS } from './constants.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import {
    type PersonalizedCollectionsContextQuery,
    type PersonalizedCollectionsContextQuery$data,
} from './__generated__/PersonalizedCollectionsContextQuery.graphql.ts';
import { getAbTestV2 } from 'dibs-ab-tests/exports/clientAbTestV2.ts';

import { type PersonalizedCollectionsContext_item$key } from './__generated__/PersonalizedCollectionsContext_item.graphql.ts';

const HOW_MANY_RECENTLY_VIEWED_ITEMS_TO_GET = 1;

function getVerticalByCategoryCode(categoryCode: string | null | undefined): string {
    const baseCategoryCode = categoryCode?.split('_')[0] || 'F';
    const categoryMap = {
        F: 'furniture',
        V: 'fashion',
        J: 'jewelry',
        A: 'art',
    };
    return categoryMap[baseCategoryCode as keyof typeof categoryMap];
}

type PersonalizedCollectionsContextValue = {
    collectionIds: string[];
    inspiredByItemId: string | null;
    backgroundColorPair: string[];
};

const PersonalizedCollectionsContext = createContext<PersonalizedCollectionsContextValue | null>(
    null
);

const personalizedCollectionsContextItemFragment = graphql`
    fragment PersonalizedCollectionsContext_item on Item {
        serviceId
        categoryCode
    }
`;

const personalizedCollectionsContextQuery = graphql`
    query PersonalizedCollectionsContextQuery($recentlyViewedItemIds: [String!]!) {
        viewer {
            personalizedCollections(itemIds: $recentlyViewedItemIds) {
                collectionId
                itemId
                relatedCollections {
                    collectionId
                }
            }
        }
    }
`;

type PersonalizedCollectionsProviderProps = {
    item: PersonalizedCollectionsContext_item$key | null | undefined;
    children: ReactNode;
};

export const PersonalizedCollectionsProvider: FC<PersonalizedCollectionsProviderProps> = ({
    item: itemRef,
    children,
}) => {
    const environment = useRelayEnvironment();
    const item = useFragment(personalizedCollectionsContextItemFragment, itemRef);
    const [data, setData] = useState<{
        result: PersonalizedCollectionsContextQuery$data | null;
    } | null>(null);
    const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);

    const isClient = typeof window !== 'undefined';
    const shouldLoad =
        isClient && getAbTestV2('PDP_PersonalizedDynamicCatalogue_Dec2025')?.variant === 'variant';

    const recentlyViewedItems = useMemo(() => {
        if (!isClient) {
            return [];
        }

        const currentItem = {
            itemId: item?.serviceId || '',
            itemCategoryCode: item?.categoryCode || 'F',
        };

        const [recentlyViewedItem] =
            getLocalRecentHistoryList(
                [currentItem.itemId],
                HOW_MANY_RECENTLY_VIEWED_ITEMS_TO_GET
            ) || [];

        if (!recentlyViewedItem) {
            return [currentItem];
        }

        return [currentItem, recentlyViewedItem];
    }, [isClient, item]);

    const recentlyViewedItemsVerticals = useMemo(
        () =>
            (recentlyViewedItems || []).map(({ itemCategoryCode }) =>
                getVerticalByCategoryCode(itemCategoryCode)
            ),
        [recentlyViewedItems]
    );

    const loadCollectionLookupQuery = useCallback(() => {
        if (!recentlyViewedItems.length) {
            return;
        }

        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
        }

        const subscription = fetchQuery<PersonalizedCollectionsContextQuery>(
            environment,
            personalizedCollectionsContextQuery,
            { recentlyViewedItemIds: recentlyViewedItems.map(rv => rv.itemId) },
            {
                fetchPolicy: 'store-or-network',
            }
        ).subscribe({
            next: result => {
                setData({ result });
            },
            error: () => {
                setData(null);
            },
            complete: () => {},
        });

        subscriptionRef.current = subscription;
    }, [environment, recentlyViewedItems]);

    useEffect(() => {
        if (shouldLoad && recentlyViewedItems.length > 0) {
            loadCollectionLookupQuery();
        }

        return () => {
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
            }
        };
    }, [loadCollectionLookupQuery, shouldLoad, recentlyViewedItems]);

    const contextValue = useMemo(() => {
        const { result } = data || {};
        const personalizedCollections = (result?.viewer?.personalizedCollections || []).filter(
            filterFalsy
        );

        if (!personalizedCollections.length) {
            return {
                collectionIds: [],
                backgroundColorPair: [],
                inspiredByItemId: null,
            };
        }

        const [firstVertical, secondVertical = getVerticalByCategoryCode(item?.categoryCode)] =
            recentlyViewedItemsVerticals || [];
        const bothItemsSameVertical = firstVertical === secondVertical;
        const backgroundColorPair = [
            COLLECTIONS_COLORS[firstVertical]?.primary,
            COLLECTIONS_COLORS[secondVertical]?.[bothItemsSameVertical ? 'secondary' : 'primary'],
        ];

        if (personalizedCollections.length === 1) {
            const [firstCollection] = personalizedCollections;
            const [firstRelatedCollection] = firstCollection?.relatedCollections || [];

            const collectionIds: string[] = [firstCollection.collectionId || ''];

            if (firstRelatedCollection) {
                collectionIds.push(firstRelatedCollection.collectionId || '');
            }

            return {
                collectionIds,
                backgroundColorPair,
                inspiredByItemId: null,
            };
        }

        const orderedCollectionIds = recentlyViewedItems.map(
            rv =>
                personalizedCollections.find(collection => collection.itemId === rv.itemId)
                    ?.collectionId || ''
        );

        const inspiredByItemId =
            recentlyViewedItems.length > 1 ? recentlyViewedItems[1].itemId : null;

        return {
            collectionIds: orderedCollectionIds,
            backgroundColorPair,
            inspiredByItemId,
        };
    }, [data, recentlyViewedItems, recentlyViewedItemsVerticals, item?.categoryCode]);

    return (
        <PersonalizedCollectionsContext.Provider value={contextValue}>
            {children}
        </PersonalizedCollectionsContext.Provider>
    );
};

export const usePersonalizedCollections = (): PersonalizedCollectionsContextValue => {
    const context = useContext(PersonalizedCollectionsContext);
    if (!context) {
        throw new Error(
            'usePersonalizedCollections must be used within PersonalizedCollectionsProvider'
        );
    }
    return context;
};
